import { TApiaApiResult, IApiaApiRequestConfig } from '@apia/api';
import { useMount } from '@apia/util';
import { usePanelIdentity } from '../common';
import { TPanelId } from '../types';
import { TOnSucceed } from './firePanelAction';
import { TBasicAction } from './basicAction';
import { useDashboardPanel } from '../dashboard/DashboardPanelContext';

type TActionListener = (result: {
  action: string;
  data: TActionResult;
  panelId: TPanelId;
}) => unknown;

export const panelActionsEmitter = new (class PanelActionsEmitter {
  listeners: Record<TPanelId, Record<string, TActionListener[]>> = {};

  on = (panelId: TPanelId, action: string, cb: TActionListener) => {
    if (!this.listeners[panelId]) this.listeners[panelId] = {};
    if (!this.listeners[panelId][action]) this.listeners[panelId][action] = [];

    this.listeners[panelId][action].push(cb);

    return () => {
      this.listeners[panelId][action] = this.listeners[panelId][action].filter(
        (current) => current !== cb,
      );
    };
  };

  emit = (panelId: TPanelId, action: string, data: TActionResult) => {
    this.listeners[panelId]?.[action]?.forEach((current) => {
      window.requestAnimationFrame(() => {
        current({ action, panelId, data });
      });
    });
  };
})();

export type TFooter = TBasicAction & { force?: boolean };

export type TCommonActionResult = { title: string; footer: TFooter };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TActionResult<T = any> = null | NonNullable<
  Awaited<
    TApiaApiResult<
      T extends Record<string, unknown> ? T & TCommonActionResult : T
    >
  >
>;

export type TActionDispatcherOptions = {
  onComplete?: TOnSucceed;
  requestConfig?: IApiaApiRequestConfig<object>;
};

export type TExternalActionDispatcher = (
  panelId: string,
  { action, ...parameters }: { action: string } & Record<string, unknown>,
  options?: TActionDispatcherOptions,
) => void;

export type TActionDispatcher = (
  { action, ...parameters }: { action: string } & Record<string, unknown>,
  options?: TActionDispatcherOptions,
) => Promise<TActionResult>;

export type TActionHandler = (result: TActionResult) => unknown;

/**
 * El hook usePanelActions es el método principal de comunicación entre el
 * módulo cliente y el módulo server de un panel.
 *
 * Acepta un actionsHandler y devuelve un actionDispatcher.
 *
 * **Existe la acción refresh** que será llamada automáticamente cada vez que
 * se guarde la configuración del panel o se haga click en el botón refrescar
 * del mismo.
 *
 * @param actionsHandler Es un objeto de tipo clave-valor en donde cada clave debe corresponderse con una acción que puede ser ejecutada en el panel. El valor debe ser una función que recibe la respuesta del servidor a la ejecución de la acción.
 *
 * @returns Un método actionDispatcher que permite ejecutar acciones para ser procesadas por la contraparte del panel en el servidor. Al ser llamado acepta un objeto que debe tener como mínimo la propiedad action: string, que indica qué acción será ejecutada. Acepta opcionalmente cualquier cantidad adicional de propiedades en el objeto.
 *
 * @example
 *
  const dispatch = usePanelActions({
    refresh() {
      console.log('Refresh');
    },
    revert(response: TActionResult<TApiaLoadText>) {
      if (response?.data?.text.label !== undefined)
        setLabel(response?.data?.text.label);
    },
  });

  return  <Button
    onClick={() => {
      dispatch({
        action: 'revert',
          text: (document.getElementById('Text') as
          HTMLInputElement).value,
      });
    }}
  >
    Revert
  </Button>
 */
export function usePanelActions(
  actionsHandler?: Record<string, TActionHandler>,
) {
  const { panelId } = usePanelIdentity();

  useMount(() => {
    const subscriptions: (() => unknown)[] = [];

    Object.entries(actionsHandler ?? {}).forEach(([action, handler]) => {
      subscriptions.push(
        panelActionsEmitter.on(panelId, action, (res) => {
          handler(res.data);
        }),
      );
    });

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  });

  const panel = useDashboardPanel();

  return panel.fireAction as TActionDispatcher;
}
